import { useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuid } from "uuid";
import { useSetRecoilState } from "recoil";
import { authState, fetchState } from "../recoil";
import { defaultAuthState } from "../recoil/authState";

/**
 * Path the server designates via the "movepage" header when the account is suspended.
 * !== 계정 정지(usrStatCd = '1') 시 서버가 movepage 헤더로 지정하는 경로 ==!
 */
export const MOVE_PAGE_SUSPENDED = "/reactivateAccount";

/**
 * Marker read by the login screen to show the suspension notice once.
 * !== 로그인 화면이 정지 안내 팝업을 한 번만 띄우도록 남기는 표식 ==!
 */
export const SUSPENDED_NOTICE_KEY = "suspended-notice";

/**
 * Clears the signed-in session without React hooks (this module is not a component).
 * !== 훅을 쓸 수 없는 모듈이라 로그아웃과 동일한 정리를 직접 수행한다. ==!
 */
const clearSession = () => {
    ["user-info", "menu-config", "menu-signed", "menu-nonSigned", "survey-ppupCheck"].forEach((key) =>
        window.sessionStorage.removeItem(key),
    );
    Cookies.remove("authorization");
    Cookies.remove("refreshauthorization");
    Cookies.remove(`authorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
    Cookies.remove(`refreshauthorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
};

/**
 * Handles the "movepage" header.
 * !== 정지 계정은 이동 대신 세션을 끊고 로그인 화면에서 안내 팝업을 띄운다. 그 외는 기존대로 이동. ==!
 */
export const handleMovePage = (movePage?: string) => {
    if (!movePage) return;

    if (movePage === MOVE_PAGE_SUSPENDED) {
        clearSession();
        window.sessionStorage.setItem(SUSPENDED_NOTICE_KEY, "Y");
        const login = window.location.origin + "/login";
        if (window.location.href !== login) window.location.href = login;
        return;
    }

    const destination = window.location.origin + movePage;
    if (window.location.href !== destination) {
        window.location.href = destination;
    }
};

// Common api
const api = axios.create();
// Set baseURL
api.defaults.baseURL = process.env.REACT_APP_API_OSCI;
// Set paramsSerializer
api.defaults.paramsSerializer = (params) => {
    const nextParams = new URLSearchParams();
    for (const key in params) {
        const value = params[key];
        if (value !== undefined) {
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    nextParams.append(key, item);
                });
            } else if (value === null) {
                nextParams.append(key, "");
            } else {
                nextParams.append(key, value);
            }
        }
    }
    return nextParams.toString();
};
// Set headers
api.interceptors.request.use(
    (config) => {
        const lang = _TEST1234.LANGUAGE;
        const authorization = _TEST1234.AUTHORIZATION;
        const refreshauthorization = _TEST1234.REFRESH_AUTHORIZATION;
        config.headers["Accept-Language"] = lang;
        if (authorization) config.headers["Authorization"] = "Bearer " + authorization;
        if (refreshauthorization) config.headers["Refreshauthorization"] = "Bearer " + refreshauthorization;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);
// Renewal authorization
api.interceptors.response.use(
    (response) => {
        const { authorization } = response.headers;
        if (authorization) {
            const token = authorization.startsWith("Bearer ") ? authorization.substr(7) : authorization;
            _TEST1234.AUTHORIZATION = token;
        }
        return response;
    },
    (error) => {
        return Promise.reject(error);
    },
);

const ApiConfig = () => {
    const setAuth = useSetRecoilState(authState);
    const setFetch = useSetRecoilState(fetchState);

    const $ = useRef({ initialized: false });

    if ($.current.initialized === false) {
        $.current.initialized = true;

        const done = (_uuid: any) => {
            if (!_uuid) return;
            setFetch((prev) => {
                const requests = { ...prev.requests };
                delete requests[_uuid];
                return { ...prev, requests };
            });
        };

        api.interceptors.request.use(
            (config) => {
                if (config.headers._disableIndicator) {
                    delete config.headers._disableIndicator;
                } else {
                    const _uuid = uuid();
                    //@ts-ignore
                    config._uuid = _uuid;
                    setFetch((prev) => {
                        const requests = { ...prev.requests, [_uuid]: true };
                        return { ...prev, requests };
                    });
                }
                return config;
            },
            (error) => {
                done(error.config._uuid);
                return Promise.reject(error);
            },
        );

        api.interceptors.response.use(
            (response) => {
                //@ts-ignore
                done(response.config._uuid);
                return response;
            },
            (error) => {
                done(error.config._uuid);
                return Promise.reject(error);
            },
        );

        /** Response interceptor */
        axios.interceptors.response.use(
            (response) => {
                switch (response.status) {
                    case 202: {
                        handleMovePage(response.headers["movepage"]);
                        break;
                    }
                }
                return response;
            },
            (error) => {
                if (error.response) {
                    handleMovePage(error.response.headers["movepage"]);

                    switch (error.response.status) {
                        // case 301: {
                        //     const movePage = error.response.headers["movepage"];
                        //     if (movePage) {
                        //         const destination = window.location.origin + movePage;
                        //         if (window.location.href !== destination) {
                        //             window.location.href = destination;
                        //         }
                        //     }
                        //     break;
                        // }
                        case 401: {
                            if (_TEST1234.AUTHORIZATION) {
                                Cookies.remove("authorization");
                                Cookies.remove("refreshauthorization");
                                Cookies.remove(`authorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
                                Cookies.remove(`refreshauthorization_${_TEST1234.SYSTEM_GROUP_ORI}`);
                                if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
                                    const authlinkKey = window.name.slice(0, -5);
                                    const domain =
                                        window.location.hostname.split(".").length > 1
                                            ? window.location.hostname.replace(
                                                  window.location.hostname.split(".")[0],
                                                  "",
                                              )
                                            : window.location.hostname;
                                    Cookies.remove(`${authlinkKey}_a`, { domain });
                                    Cookies.remove(`${authlinkKey}_r`, { domain });
                                }

                                window.sessionStorage.removeItem("user-info");
                                setAuth({
                                    ...defaultAuthState,
                                    isInitialized: true,
                                });
                                window.location.href = window.location.origin + "/login";
                            }

                            break;
                        }
                    }
                }

                return Promise.reject(error);
            },
        );
    }

    return null;
};

/* 임시 공통 개발용 */
const _TEST1234 = {
    NODE_ENV: process.env.NODE_ENV,
    LANGUAGES: ["en", "tz", "ko"],
    SYSTEM_GROUPS: ["tancis", "tanesw", "tanoga"],
    get LANGUAGE() {
        const lang = this.LANGUAGES.find((value) => value === Cookies.get("lang")) || "en";
        return lang.toUpperCase();
    },
    get SYSTEM_GROUP_ENV() {
        return this.SYSTEM_GROUPS.find((value) => value === process.env.REACT_APP_SYSTEM_GROUP) || "";
    },
    get SYSTEM_GROUP_ORI() {
        // TODO : 기존 시스템 체크 부분 수정
        const subdomain = process.env.REACT_APP_SYSTEM_GROUP || "";
        switch (this.NODE_ENV) {
            case "production": {
                //const subdomain = window.location.hostname.substring(0, window.location.hostname.indexOf("."));
                let group;
                group = "tanoga";
                if (group) return group;
                else break;
            }
            default:
                return this.SYSTEM_GROUP_ENV;
        }
    },
    get PORTAL_API_ENV() {
        return process.env.REACT_APP_API_OSCI || "";
    },
    get PORTAL_API_ORI() {
        return process.env.REACT_APP_API_OSCI || "";
    },
    get SYSTEM() {
        return "external";
    },
    get AUTHORIZATION() {
        return Cookies.get("authorization") || "";
        // switch (this.NODE_ENV) {
        //     case "production": {
        //         if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
        //             const authlinkKey = window.name.slice(0, -5);
        //             return Cookies.get(`${authlinkKey}_a`) || "";
        //         }
        //         return Cookies.get("authorization") || "";
        //     }
        //     default: {
        //         if (Cookies.get("oga") === "true") {
        //             return Cookies.get(`authorization_tanoga`) || "";
        //         }
        //         return Cookies.get(`authorization_${this.SYSTEM_GROUP_ORI}`) || "";
        //     }
        // }
    },
    get REFRESH_AUTHORIZATION() {
        return Cookies.get("refreshauthorization") || "";
        // 2026. 05. 21. 파트별 토큰을 사용하고 있지만 지금은 아님 하나만 사용 Key : authorization
        // switch (this.NODE_ENV) {
        //     case "production": {
        //         if (window.name.length === 41 && window.name.indexOf("-link") === 36) {
        //             const authlinkKey = window.name.slice(0, -5);
        //             return Cookies.get(`${authlinkKey}_r`) || "";
        //         }
        //         return Cookies.get("refreshauthorization") || "";
        //     }
        //     default: {
        //         if (Cookies.get("oga") === "true") {
        //             return Cookies.get(`refreshauthorization_tanoga`) || "";
        //         }
        //         return Cookies.get(`refreshauthorization_${this.SYSTEM_GROUP_ORI}`) || "";
        //     }
        // }
    },
    set AUTHORIZATION(value) {
        Cookies.set("authorization", value, { expires: 30 });
        // if (this.NODE_ENV !== "production") {
        //     Cookies.set(`authorization_${this.SYSTEM_GROUP_ORI}`, value, { expires: 30 });
        // }
    },
    set REFRESH_AUTHORIZATION(value) {
        Cookies.set("refreshauthorization", value, { expires: 30 });
        // if (this.NODE_ENV !== "production") {
        //     Cookies.set(`refreshauthorization_${this.SYSTEM_GROUP_ORI}`, value, { expires: 30 });
        // }
    },
    get PORTAL_TYPE() {
        return "osci";
    },
};

export { api, _TEST1234 };

export default ApiConfig;
